import { useEffect, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Container } from '@/components/layout/Container'
import { Button } from '@/components/ui/Button'
import { getOrderStatus, cancelOrder, verifyOrder, type OrderStatus as OrderStatusData } from '@/lib/api'
import { formatPrice } from '@/lib/utils'
import { useUIStore } from '@/stores/uiStore'
import { CheckCircle2, Clock, Package, Truck, XCircle, AlertCircle, ArrowLeft } from 'lucide-react'

const statusOrder = ['pending', 'paid', 'processing', 'shipped', 'delivered', 'cancelled', 'refunded']

const statusMeta: Record<
  string,
  { label: string; icon: React.ElementType; color: string }
> = {
  pending: { label: 'Order placed', icon: Clock, color: 'text-carbon/60' },
  paid: { label: 'Payment confirmed', icon: CheckCircle2, color: 'text-carbon' },
  processing: { label: 'Preparing your order', icon: Package, color: 'text-carbon' },
  shipped: { label: 'Shipped', icon: Truck, color: 'text-carbon' },
  delivered: { label: 'Delivered', icon: CheckCircle2, color: 'text-carbon' },
  cancelled: { label: 'Cancelled', icon: XCircle, color: 'text-wd-orange' },
  refunded: { label: 'Refunded', icon: AlertCircle, color: 'text-wd-orange' },
}

export function OrderStatus() {
  const [searchParams] = useSearchParams()
  const addToast = useUIStore((s) => s.addToast)
  // Paystack appends both `trxref` and `reference` to the callback URL, and a
  // stale `?reference=` in the query can leave an empty first value. Prefer the
  // first non-empty reference, then trxref.
  const reference =
    [...searchParams.getAll('reference'), ...searchParams.getAll('trxref')].find(
      (value) => value.trim() !== ''
    ) || undefined

  const [order, setOrder] = useState<OrderStatusData | null>(null)
  const [loading, setLoading] = useState(true)
  const [verifying, setVerifying] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!reference) {
      setLoading(false)
      return
    }
    let cancelled = false
    let verifyInterval: ReturnType<typeof setInterval> | null = null

    const fetchOrder = async () => {
      try {
        const data = await getOrderStatus(reference)
        if (cancelled) return
        setOrder(data)
        // If order is still pending, try to verify with Paystack
        if (data && ['pending'].includes(data.status) && !verifying) {
          setVerifying(true)
          let attempts = 0
          const maxAttempts = 10
          verifyInterval = setInterval(async () => {
            attempts++
            try {
              const result = await verifyOrder(reference)
              if (result.status === 'captured') {
                if (verifyInterval) clearInterval(verifyInterval)
                verifyInterval = null
                const updated = await getOrderStatus(reference)
                if (!cancelled) {
                  setOrder(updated)
                  setVerifying(false)
                  addToast('Payment confirmed. Welcome to Little Sky.', 'success')
                }
                return
              }
              if (result.status === 'failed' || result.status === 'abandoned') {
                if (verifyInterval) clearInterval(verifyInterval)
                verifyInterval = null
                if (!cancelled) {
                  setVerifying(false)
                  setError(`Payment ${result.status}.`)
                }
              }
            } catch {
              // ignore
            }
            if (attempts >= maxAttempts) {
              if (verifyInterval) clearInterval(verifyInterval)
              verifyInterval = null
              if (!cancelled) {
                setVerifying(false)
              }
            }
          }, 3000)
        } else if (data && ['paid', 'captured', 'processing', 'shipped', 'delivered'].includes(data.status)) {
          setVerifying(false)
          if (verifyInterval) {
            clearInterval(verifyInterval)
            verifyInterval = null
          }
        }
      } catch {
        if (!cancelled) {
          setOrder(null)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchOrder()
    return () => {
      cancelled = true
      if (verifyInterval) clearInterval(verifyInterval)
    }
  }, [reference])

  const handleCancel = async () => {
    if (!reference || !order?.can_cancel) return
    setCancelling(true)
    try {
      const result = await cancelOrder(reference)
      if (result.success) {
        addToast('Order cancelled.', 'success')
        const updated = await getOrderStatus(reference)
        setOrder(updated)
      } else {
        addToast(result.error || 'Could not cancel order.', 'error')
      }
    } finally {
      setCancelling(false)
    }
  }

  if (!reference) {
    return (
      <Container className="py-32 text-center">
        <h1 className="font-display text-3xl text-carbon">No order reference</h1>
        <Link to="/shop" className="mt-4 inline-block text-carbon hover:underline">
          Continue shopping
        </Link>
      </Container>
    )
  }

  if (loading) {
    return (
      <Container className="py-32 text-center">
        <p className="text-carbon/60">Loading order status…</p>
      </Container>
    )
  }

  if (!order) {
    return (
      <Container className="py-32 text-center">
        <h1 className="font-display text-3xl text-carbon">Order not found</h1>
        <p className="mt-2 text-carbon/60">We couldn't locate {reference}.</p>
        <Link to="/shop" className="mt-4 inline-block text-carbon hover:underline">
          Continue shopping
        </Link>
      </Container>
    )
  }

  const currentIndex = statusOrder.indexOf(order.status)
  const timeline = order.timeline?.length
    ? order.timeline
    : [{ status: order.status, at: Date.now() / 1000 }]

  const isPaid = ['paid', 'processing', 'shipped', 'delivered'].includes(order.status)

  return (
    <div className="pt-28 pb-24">
      <Container className="max-w-3xl">
        <Link
          to="/shop"
          className="mb-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-carbon/60 hover:text-wd-orange"
        >
          <ArrowLeft className="h-4 w-4" /> Back to shop
        </Link>

        {verifying && (
          <div className="mb-6 bg-wd-mushroom p-4 text-sm text-carbon">
            Confirming payment with Paystack…
          </div>
        )}
        {error && (
          <div className="mb-6 border border-line bg-card p-4 text-sm text-carbon">
            {error}
          </div>
        )}
        {isPaid && !verifying && !error ? (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="mb-10 border border-ink/10 bg-wd-mist p-8 text-center"
          >
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-wd-orange text-white">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h1 className="mb-2 font-display text-4xl normal-case text-carbon">Order confirmed</h1>
            <p className="text-carbon/60">
              Thank you. We have received your payment and are preparing your order.
            </p>
            <p className="mt-4 text-sm font-medium text-carbon">
              Order reference: {order.reference}
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
              <Link to="/shop">
                <Button className="w-full sm:w-auto">Continue shopping</Button>
              </Link>
              <Link to="/account">
                <Button variant="secondary" className="w-full sm:w-auto">
                  View account
                </Button>
              </Link>
            </div>
          </motion.div>
        ) : !isPaid ? (
          <>
            <h1 className="mb-2 font-display text-4xl normal-case text-carbon">Order {order.reference}</h1>
            <p className="mb-8 text-sm text-carbon/60">
              Status: <span className="font-medium text-carbon">{statusMeta[order.status]?.label || order.status}</span>
            </p>
          </>
        ) : null}

        <div className="mb-10 space-y-6">
          {timeline.map((event, i) => {
            const meta = statusMeta[event.status] || statusMeta.pending
            const Icon = meta.icon
            const isCurrent = i === timeline.length - 1
            return (
              <div key={`${event.status}-${i}`} className="flex items-start gap-4">
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center border ${
                    isCurrent ? 'border-ink bg-ink text-white' : 'border-ink/10 text-carbon/40'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <p className={`font-display text-lg normal-case ${meta.color}`}>
                    {meta.label}
                  </p>
                  {event.note && <p className="text-sm text-carbon/60">{event.note}</p>}
                  <p className="text-xs text-carbon/40">
                    {new Date(event.at * 1000).toLocaleString('en-NG', {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}
                  </p>
                </div>
              </div>
            )
          })}
        </div>

        {order.shipping && (order.shipping.tracking_number || order.shipping.estimated_min) && (
          <div className="mb-10 border border-ink/10 bg-wd-mist p-6">
            <h2 className="mb-4 font-display text-xl normal-case text-carbon">Shipping</h2>
            {order.shipping.tracking_number && (
              <p className="text-sm text-carbon/60">
                Tracking: <span className="font-medium text-carbon">{order.shipping.tracking_number}</span>
              </p>
            )}
            {order.shipping.label_url && (
              <a
                href={order.shipping.label_url}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-block text-sm text-carbon hover:underline"
              >
                Track package
              </a>
            )}
            {order.shipping.estimated_min && order.shipping.estimated_max && (
              <p className="mt-2 text-sm text-carbon/60">
                Estimated delivery: {order.shipping.estimated_min}–{order.shipping.estimated_max} business days
              </p>
            )}
          </div>
        )}

        <div className="mb-10 border border-ink/10 bg-wd-mist p-6">
          <h2 className="mb-4 font-display text-xl normal-case text-carbon">Items</h2>
          <div className="space-y-4">
            {order.items.map((item, i) => (
              <div key={i} className="flex items-center gap-4">
                {item.image && (
                  <img src={item.image} alt={item.name} className="h-16 w-14 object-cover" />
                )}
                <div className="flex-1">
                  <p className="font-display text-lg normal-case text-carbon">{item.name.toLowerCase()}</p>
                  <p className="text-sm text-carbon/60">{item.size} × {item.quantity}</p>
                </div>
                <span className="text-carbon">{formatPrice(item.price_kobo / 100)}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 flex justify-between text-lg text-carbon">
            <span className="font-display">Total</span>
            <span className="font-display">{formatPrice(order.total_kobo / 100)}</span>
          </div>
        </div>

        {order.can_cancel && currentIndex <= 1 && (
          <Button
            variant="secondary"
            isLoading={cancelling}
            onClick={handleCancel}
            className="w-full sm:w-auto"
          >
            Cancel order
          </Button>
        )}
      </Container>
    </div>
  )
}
